import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MDSpinner from "react-md-spinner";
import { toast } from "react-toastify";

import { fetchPluviometerRainSheet } from "../../utils/farmPluviometryApi";
import {
  buildRainYearFileName,
  exportRainMonthPdf,
  printRainMonthSheet,
} from "../../utils/rainMonthExport";
import { formatDateInputBr, formatRainMm } from "../../utils/rainHeatmapUtils";
import PluviometerDayEditModal from "./PluviometerDayEditModal";
import RainSheetExportActions from "./RainSheetExportActions";

import "./PluviometerRainSheet.css";

const MONTHS = [
  { id: 1, label: "Jan" },
  { id: 2, label: "Fev" },
  { id: 3, label: "Mar" },
  { id: 4, label: "Abr" },
  { id: 5, label: "Mai" },
  { id: 6, label: "Jun" },
  { id: 7, label: "Jul" },
  { id: 8, label: "Ago" },
  { id: 9, label: "Set" },
  { id: 10, label: "Out" },
  { id: 11, label: "Nov" },
  { id: 12, label: "Dez" },
];

const DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function buildDateKey(year, month, day) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

function PluviometerRainSheet({
  farmId,
  gauge,
  onEditGauge,
  onDailySaved,
  onClose,
}) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [sheet, setSheet] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const sheetRef = useRef(null);

  const yearOptions = useMemo(() => {
    const options = [];
    for (let value = currentYear; value >= currentYear - 5; value -= 1) {
      options.push(value);
    }
    return options;
  }, [currentYear]);

  const loadSheet = useCallback(async () => {
    if (!gauge || !gauge.stationUuid) return;
    setLoading(true);
    setLoadError("");
    try {
      const data = await fetchPluviometerRainSheet(
        farmId,
        gauge.stationUuid,
        year
      );
      setSheet(data);
    } catch (error) {
      setSheet(null);
      setLoadError(t("Não foi possível carregar a planilha pluviométrica."));
    } finally {
      setLoading(false);
    }
  }, [farmId, gauge, year, t]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  function handleCellSaved(result) {
    setEditingCell(null);
    if (onDailySaved) {
      onDailySaved(result);
    }
    if (!result || !result.date) {
      loadSheet();
      return;
    }

    setSheet((current) => {
      if (!current) return current;
      const nextCells = { ...(current.cells || {}) };
      const rainValue = Number(result.rainMm) || 0;
      if (rainValue > 0) {
        nextCells[result.date] = {
          rainMm: rainValue,
          readingUuid: result.readingUuid,
        };
      } else {
        delete nextCells[result.date];
      }

      const monthTotals = {};
      const monthAverages = {};
      MONTHS.forEach((month) => {
        const maxDay = daysInMonth(year, month.id);
        let total = 0;
        for (let day = 1; day <= maxDay; day += 1) {
          const key = buildDateKey(year, month.id, day);
          if (nextCells[key]) {
            total += Number(nextCells[key].rainMm) || 0;
          }
        }
        monthTotals[String(month.id)] = Math.round(total * 10) / 10;
        monthAverages[String(month.id)] =
          Math.round((total / maxDay) * 100) / 100;
      });

      return {
        ...current,
        cells: nextCells,
        monthlyTotals: monthTotals,
        monthlyAverages: monthAverages,
      };
    });
  }

  if (!gauge) return null;

  const cells = (sheet && sheet.cells) || {};
  const monthlyTotals = (sheet && sheet.monthlyTotals) || {};
  const monthlyAverages = (sheet && sheet.monthlyAverages) || {};
  const lastRainLabel = gauge.lastRainDate
    ? t("Última chuva: {{date}}", {
        date: formatDateInputBr(gauge.lastRainDate),
      })
    : "";

  function handlePrint() {
    if (!sheetRef.current) return;
    if (!printRainMonthSheet(sheetRef.current)) {
      return;
    }
  }

  async function handleExportPdf() {
    if (!sheetRef.current || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportRainMonthPdf(
        sheetRef.current,
        buildRainYearFileName({
          stationLabel: gauge.label,
          year,
        })
      );
      toast.success(t("PDF da planilha salvo."));
    } catch (error) {
      toast.error(t("Não foi possível exportar o PDF da planilha."));
    } finally {
      setExportingPdf(false);
    }
  }

  return (
    <section className="pluviometer-rain-sheet">
      <header className="pluviometer-rain-sheet__header">
        <div>
          <h3>{t("Planilha pluviométrica")}</h3>
          <p>
            {gauge.label}
            {gauge.stationNumber ? ` · #${gauge.stationNumber}` : ""}
            {lastRainLabel ? ` · ${lastRainLabel}` : ""}
          </p>
        </div>
        <div className="pluviometer-rain-sheet__header-actions">
          <label className="pluviometer-rain-sheet__year">
            {t("Ano")}
            <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
              {yearOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <RainSheetExportActions
            onPrint={handlePrint}
            onExportPdf={handleExportPdf}
            exportingPdf={exportingPdf}
            disabled={loading || Boolean(loadError)}
          />
          {onEditGauge ? (
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => onEditGauge(gauge)}
            >
              {t("Editar pluviômetro")}
            </button>
          ) : null}
          {onClose ? (
            <button type="button" className="btn btn-default btn-sm" onClick={onClose}>
              {t("Fechar")}
            </button>
          ) : null}
        </div>
      </header>

      <p className="pluviometer-rain-sheet__hint">
        {t(
          "Clique em um valor para registrar ou corrigir a chuva daquele dia (mm)."
        )}
      </p>

      {loading ? (
        <div className="pluviometer-rain-sheet__loading">
          <MDSpinner singleColor="#1ABB9C" size={28} />
        </div>
      ) : loadError ? (
        <div className="pluviometer-rain-sheet__error">{loadError}</div>
      ) : (
        <div ref={sheetRef} className="pluviometer-rain-sheet__print-area">
          <div className="pluviometer-rain-sheet__print-title">
            <h2>{t("Planilha pluviométrica")}</h2>
            <p>
              {gauge.label}
              {gauge.stationNumber ? ` · #${gauge.stationNumber}` : ""}
              {" · "}
              {year}
              {lastRainLabel ? ` · ${lastRainLabel}` : ""}
            </p>
          </div>
        <div className="pluviometer-rain-sheet__scroll">
          <table className="pluviometer-rain-sheet__table">
            <thead>
              <tr>
                <th scope="col">{t("Dia")}</th>
                {MONTHS.map((month) => (
                  <th key={month.id} scope="col">
                    {month.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day) => (
                <tr key={day}>
                  <th scope="row">{day}</th>
                  {MONTHS.map((month) => {
                    const maxDay = daysInMonth(year, month.id);
                    if (day > maxDay) {
                      return (
                        <td
                          key={month.id}
                          className="pluviometer-rain-sheet__cell pluviometer-rain-sheet__cell--empty"
                        />
                      );
                    }

                    const dateKey = buildDateKey(year, month.id, day);
                    const cell = cells[dateKey];
                    const mm = cell ? Number(cell.rainMm) : null;

                    return (
                      <td key={month.id} className="pluviometer-rain-sheet__cell">
                        <button
                          type="button"
                          className={
                            mm != null && mm > 0
                              ? "pluviometer-rain-sheet__value pluviometer-rain-sheet__value--filled"
                              : "pluviometer-rain-sheet__value"
                          }
                          title={dateKey}
                          onClick={() =>
                            setEditingCell({
                              dateStr: dateKey,
                              initialMm: mm != null ? mm : "",
                            })
                          }
                        >
                          {mm != null && mm > 0 ? formatRainMm(mm) : "—"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">{t("Total")}</th>
                {MONTHS.map((month) => (
                  <td key={`total-${month.id}`} className="pluviometer-rain-sheet__footer">
                    {formatRainMm(monthlyTotals[String(month.id)] || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">{t("Média")}</th>
                {MONTHS.map((month) => (
                  <td key={`avg-${month.id}`} className="pluviometer-rain-sheet__footer">
                    {formatRainMm(monthlyAverages[String(month.id)] || 0)}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        </div>
      )}

      {editingCell ? (
        <PluviometerDayEditModal
          farmId={farmId}
          stationUuid={gauge.stationUuid}
          stationLabel={gauge.label}
          dateStr={editingCell.dateStr}
          initialMm={editingCell.initialMm}
          onClose={() => setEditingCell(null)}
          onSaved={handleCellSaved}
        />
      ) : null}
    </section>
  );
}

export default PluviometerRainSheet;
